import React from 'react';
import cn from 'classnames';
import { IBaseElementProps } from 'tbk-components/src/components/BasicElement';
import Button, { ILink } from 'tbk-components/src/components/Button';
import Image, { IImage } from 'tbk-components/src/components/Image';
import Navbar, { INavBarProps } from 'tbk-components/src/components/NavBar';
import Nav, { NavItem } from 'tbk-components/src/components/Nav';
import Offcanvas from 'tbk-components/src/components/Offcanvas';
import SocialIcons, {
	ISocialIconsProps,
} from 'tbk-components/src/components/SocialIcons';

export interface IHeaderProps extends IBaseElementProps {
	logo?: IImage;
	logoURL?: string;
	mainNav?: NavItem[];
	socialNav?: ISocialIconsProps;
	prayerlineLink?: ILink;
	donateCalloutText?: string;
	donateCalloutNav?: NavItem[];
	homeURL?: string;
	myAccountLink?: string;
	cartURL?: string;
	cartAmount?: number;
	programCodePageURL?: string;
}

const Header: React.FC<IHeaderProps> = ({
	className,
	classNames = [],
	logo,
	logoURL,
	mainNav,
	socialNav,
	prayerlineLink,
	donateCalloutText,
	donateCalloutNav,
	homeURL,
	myAccountLink,
	cartURL,
	cartAmount = 0,
	programCodePageURL,
}) => {
	const onProgramCodeSubmit = (e) => {
		e.preventDefault();
		// redirect to the program entry page
		window.location.href =
			programCodePageURL + '#!code=' + e.target[0].value.toUpperCase();
	};

	return (
		<header className={className || cn('header', ...classNames)}>
			<div className="header-top-desktop container-fluid">
				<div className="header-top-desktop__content d-none d-sm-flex">
					<div className="header-top-desktop__social-nav">
						{socialNav && (
							<SocialIcons classNames={['nav']} {...socialNav} />
						)}
						{myAccountLink && (
							<a
								id="my-account-link"
								href={myAccountLink}
								target="_blank"
								aria-label="My Account"
							>
								<i className="icon icon-person-icon"></i>
							</a>
						)}
						<a
							id="shopping-cart-link"
							href={cartURL}
							target="_blank"
							aria-label="Shopping Cart"
						>
							<div className="cart-amount-wrapper" />
							<i className="icon icon-shopping-cart-solid"></i>
						</a>
					</div>
					<div className="header-top-desktop__search">
						<form
							className="header-top-desktop__search-form d-flex"
							action={homeURL}
							method="get"
						>
							<input
								name="s"
								className="form-control"
								type="text"
								placeholder="Search..."
								aria-label="Search"
							/>
							<button className="btn" type="submit">
								<i className="icon-search" />
								<span className="visually-hidden">Search</span>
							</button>
						</form>
					</div>
				</div>
			</div>
			<Navbar
				logo={logo}
				logoURL={logoURL}
				background={'white'}
				useCollapse={false}
				useOffcanvas={true}
				isSticky={false}
				className="navbar navbar-expand-xl navbar-white bg-white"
			>
				{mainNav && (
					<Nav
						navItems={mainNav}
						collapseEnabled={false}
						dropdownEnabled={true}
						classNames={['main-nav-menu']}
					/>
				)}
				<div className="main-nav-callout">
					{prayerlineLink && (
						<Button
							type="secondary"
							size="lg"
							classNames={['main-nav-callout__link']}
							{...prayerlineLink}
						/>
					)}
					{donateCalloutText && (
						<div className="main-nav-callout__dropdown dropdown">
							<a
								className="btn btn-primary btn-lg"
								href="#"
								role="button"
								id="desktop-header__callout-link"
								data-bs-toggle="dropdown"
								aria-expanded="false"
							>
								{donateCalloutText}
							</a>
							{donateCalloutNav.length > 0 && (
								<ul
									className="main-nav-callout__dropdown-menu dropdown-menu dropdown-menu-end"
									aria-labelledby="desktop-header__callout-link"
								>
									<li>
										<div className="donate-widget">
											<h5 className="donate-widget__heading">
												Have a code?
											</h5>
											<p className="donate-widget__tagline">
												Enter it and donate now
											</p>
											<form
												className="donate-widget__form"
												onSubmit={(e) => {
													onProgramCodeSubmit(e);
												}}
											>
												<div className="form-group">
													<input
														className="form-control"
														type="text"
														placeholder="Enter code (##AA-##-##)"
														aria-label="Enter code"
													/>
												</div>
												<button
													className="donate-widget__form-btn btn btn-sm btn-secondary"
													type="submit"
												>
													Go
												</button>
											</form>
										</div>
									</li>
									{donateCalloutNav.map(
										(calloutLink, index) => (
											<li key={index}>
												<a
													className="dropdown-item"
													href={calloutLink.href}
												>
													{calloutLink.label}
												</a>
											</li>
										),
									)}
								</ul>
							)}
						</div>
					)}
				</div>
			</Navbar>
			<Offcanvas
				headingImage={{ ...logo }}
				classNames={['mobile-offcanvas-container']}
			>
				<div className="header-top-mobile d-sm-none">
					<div className="header-top-mobile__search">
						<form
							className="header-top-mobile__search-form d-flex"
							action={homeURL}
							method="get"
						>
							<input
								name="s"
								className="form-control"
								type="text"
								placeholder="Search..."
								aria-label="Search"
							/>
							<button className="btn" type="submit">
								<i className="icon-search" />
								<span className="visually-hidden">Search</span>
							</button>
						</form>
					</div>
					<div className="header-top-mobile__social-nav">
						<a
							id="shopping-cart-link-mobile"
							href={cartURL}
							target="_blank"
							aria-label="Shopping Cart"
						>
							<div className="cart-amount-wrapper" />
							<i className="icon icon-shopping-cart-solid"></i>
						</a>
					</div>
				</div>
				{mainNav && (
					<Nav
						navItems={mainNav}
						collapseEnabled={true}
						dropdownEnabled={false}
						classNames={['main-nav-menu-mobile']}
					/>
				)}
				<div className="main-nav-mobile-callout">
					{prayerlineLink && (
						<Button
							type="secondary"
							size="lg"
							classNames={['main-nav-mobile-callout__link']}
							{...prayerlineLink}
						/>
					)}
					{donateCalloutNav.length > 0 && (
						<div
							className="main-nav-mobile-callout__dropdown-menu"
							aria-labelledby="desktop-header__callout-link"
						>
							<div className="donate-widget">
								<h5 className="donate-widget__heading">
									Have a code?
								</h5>
								<p className="donate-widget__tagline">
									Enter it and donate now
								</p>
								<form
									onSubmit={(e) => {
										onProgramCodeSubmit(e);
									}}
									className="donate-widget__form"
								>
									<div className="form-group">
										<input
											className="form-control"
											type="text"
											placeholder="Enter code (###AAA-##-##)"
											aria-label="Enter code"
										/>
									</div>
									<button
										className="donate-widget__form-btn btn btn-sm btn-secondary"
										type="submit"
									>
										Go
									</button>
								</form>
							</div>
							<ul>
								{donateCalloutNav.map((calloutLink, index) => (
									<li key={index}>
										<a
											className="dropdown-item"
											href={calloutLink.href}
										>
											{calloutLink.label}
										</a>
									</li>
								))}
							</ul>
						</div>
					)}
				</div>
				<div className="header-bottom-mobile d-sm-none">
					{socialNav && (
						<SocialIcons classNames={['nav']} {...socialNav} />
					)}
				</div>
			</Offcanvas>
		</header>
	);
};

export default Header;
